<script lang="ts">
  import { onMount } from 'svelte';
  import { _ } from 'svelte-i18n';
  import type { PageData } from './$types';
  import ContentCard from '$lib/components/content/ContentCard.svelte';
  import RelatedContent from '$lib/components/content/RelatedContent.svelte';
  import SocialShare from '$lib/components/content/SocialShare.svelte';
  
  interface Props {
    data: PageData;
  }
  
  let { data }: Props = $props();
  
  onMount(() => {
    // Track page view
    if (typeof window !== 'undefined' && window.plausible) {
      window.plausible('Changelog Entry View', {
        props: { 
          slug: data.entry.slug,
          version: data.entry.version,
          type: data.entry.type,
          category: data.entry.category,
          locale: data.locale
        }
      });
    }
  });
  
  function formatDate(dateString: string, locale: string): string {
    const date = new Date(dateString);
    const localeCode = locale.split('-')[0];
    
    return date.toLocaleDateString(localeCode, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }
  
  function getTypeIcon(type: string): string {
    switch (type.toLowerCase()) {
      case 'feature': return '✨';
      case 'improvement': return '⚡';
      case 'bugfix': return '🐛';
      case 'security': return '🔒';
      default: return '🚀';
    }
  }
  
  // Share the canonical production URL (a plain value — not a function, and not
  // the localhost origin — so "Copy Link" copies the real heyweek.com URL).
  const shareUrl = $derived(`https://heyweek.com${data.meta.canonical}`);

  // The markdown body starts with "# <title>", which marked() renders as an
  // <h1> — a duplicate of the page header's <h1>. Strip that leading h1 so the
  // page has exactly one (Ahrefs "Multiple H1 tags").
  const bodyContent = $derived(
    data.entry.content.replace(/^\s*<h1[^>]*>[\s\S]*?<\/h1>\s*/i, '')
  );
</script>

<svelte:head>
  <title>{data.meta.title}</title>
  <meta name="description" content={data.meta.description} />
  <link rel="canonical" href="https://heyweek.com{data.meta.canonical}" />

  <!-- Open Graph -->
  <meta property="og:title" content={data.meta.title} />
  <meta property="og:description" content={data.meta.description} />
  <meta property="og:type" content="article" />
  <meta property="og:url" content="https://heyweek.com{data.meta.canonical}" />
  <meta property="article:author" content={data.meta.author} />
  <meta property="article:published_time" content={data.meta.publishedTime} />
  <meta property="article:modified_time" content={data.meta.modifiedTime} />
  <meta property="article:section" content={data.meta.category} />
  {#each data.meta.tags as tag}
    <meta property="article:tag" content={tag} />
  {/each}
  
  <!-- Twitter Card -->
  <meta name="twitter:title" content={data.meta.title} />
  <meta name="twitter:description" content={data.meta.description} />
  
  <!-- JSON-LD Structured Data (rendered via @html so the JSON is actually evaluated;
       a plain {expr} inside a <script> tag is emitted as raw text by Svelte). -->
  {@html `<script type="application/ld+json">${JSON.stringify({
    "@context": "https://schema.org",
    "@type": "TechArticle",
    "headline": data.meta.title,
    "description": data.meta.description,
    "author": {
      "@type": "Organization",
      "name": data.meta.author
    },
    "publisher": {
      "@type": "Organization",
      "name": "Heyweek",
      "logo": {
        "@type": "ImageObject",
        "url": "https://heyweek.com/images/logo.png"
      }
    },
    "datePublished": data.meta.publishedTime,
    "dateModified": data.meta.modifiedTime,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://heyweek.com${data.meta.canonical}`
    }
  }).replace(/</g, '\\u003c')}</` + `script>`}
</svelte:head>

<article class="changelog-entry">
  <div class="container">
    <div class="changelog-entry__layout">
      <!-- Main Content -->
      <div class="changelog-entry__main">
        <!-- Header -->
        <header class="changelog-entry__header">
          <div class="changelog-entry__breadcrumb">
            <a href="/changelog" class="breadcrumb__link">{$_('changelog.title')}</a>
            <span class="breadcrumb__separator">→</span>
            <span class="breadcrumb__current">{data.entry.version}</span>
          </div>
          
          <div class="changelog-entry__meta">
            <span class="changelog-entry__type">
              <span class="changelog-entry__type-icon">{getTypeIcon(data.entry.type)}</span>
              {data.entry.type}
            </span>
            <span class="changelog-entry__version">{data.entry.version}</span>
            <time class="changelog-entry__date" datetime={data.entry.date}>
              {formatDate(data.entry.date, data.locale)}
            </time>
          </div>
          
          <h1 class="changelog-entry__title">{data.entry.title}</h1>
          
          <div class="changelog-entry__info">
            <div class="changelog-entry__details">
              <span class="changelog-entry__category">📂 {data.entry.category}</span>
            </div>
            
            <SocialShare 
              url={shareUrl}
              title={data.entry.title}
              description={data.entry.excerpt}
            />
          </div>
          
          {#if data.entry.tags.length > 0}
            <div class="changelog-entry__tags">
              {#each data.entry.tags as tag}
                <span class="changelog-entry__tag">#{tag}</span>
              {/each}
            </div>
          {/if}
        </header>
        
        <!-- Content -->
        <div class="changelog-entry__content">
          {@html bodyContent}
        </div>
        
        <!-- Footer -->
        <footer class="changelog-entry__footer">
          <div class="changelog-entry__share">
            <h3 class="share__title">{$_('changelog.share.title')}</h3>
            <SocialShare 
              url={shareUrl}
              title={data.entry.title}
              description={data.entry.excerpt}
              showLabels={true}
            />
          </div>
        </footer>
      </div>
      
      <!-- Sidebar -->
      <aside class="changelog-entry__sidebar">
        <!-- Recent Entries -->
        {#if data.recentEntries.length > 0}
          <div class="sidebar-section">
            <h3 class="sidebar-section__title">{$_('changelog.sidebar.recent')}</h3>
            <div class="sidebar-section__content">
              {#each data.recentEntries as entry}
                <div class="recent-entry">
                  <div class="recent-entry__meta">
                    <span class="recent-entry__type">
                      {getTypeIcon(entry.type)} {entry.type}
                    </span>
                    <span class="recent-entry__version">{entry.version}</span>
                  </div>
                  <a href="/changelog/{entry.slug}" class="recent-entry__title">
                    {entry.title}
                  </a>
                  <time class="recent-entry__date" datetime={entry.date}>
                    {formatDate(entry.date, data.locale)}
                  </time>
                </div>
              {/each}
            </div>
          </div>
        {/if}
      </aside>
    </div>
  </div>
</article>

<!-- Related Entries -->
{#if data.relatedEntries.length > 0}
  <section class="related-entries">
    <div class="container">
      <RelatedContent
        title={$_('changelog.related.title')}
        items={data.relatedEntries}
        type="changelog"
        locale={data.locale}
      />
    </div>
  </section>
{/if}

<style>
  .changelog-entry {
    padding: var(--space-8) 0;
    min-height: 100vh;
  }
  
  .changelog-entry__layout {
    display: grid;
    grid-template-columns: 1fr 300px;
    gap: var(--space-8);
    align-items: start;
  }
  
  .changelog-entry__main {
    max-width: none;
  }
  
  .changelog-entry__header {
    margin-bottom: var(--space-8);
    padding-bottom: var(--space-6);
    border-bottom: 1px solid var(--border-light);
  }
  
  .changelog-entry__breadcrumb {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    margin-bottom: var(--space-4);
    font-size: var(--font-size-sm);
  }
  
  .breadcrumb__link {
    color: var(--brand-primary);
    text-decoration: none;
    transition: var(--transition-colors);
  }
  
  .breadcrumb__link:hover {
    color: var(--brand-primary-dark);
  }
  
  .breadcrumb__separator {
    color: var(--text-tertiary);
  }
  
  .breadcrumb__current {
    color: var(--text-secondary);
  }
  
  .changelog-entry__meta {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    margin-bottom: var(--space-4);
    font-size: var(--font-size-sm);
    flex-wrap: wrap;
  }
  
  .changelog-entry__type {
    display: flex;
    align-items: center;
    gap: var(--space-1);
    font-weight: var(--font-weight-medium);
    color: var(--text-tertiary);
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }
  
  .changelog-entry__type-icon {
    font-size: var(--font-size-base);
  }
  
  .changelog-entry__version {
    background: var(--brand-primary);
    color: white;
    padding: var(--space-1) var(--space-3);
    border-radius: var(--radius-full);
    font-weight: var(--font-weight-medium);
    font-size: var(--font-size-xs);
  }
  
  .changelog-entry__date {
    color: var(--text-tertiary);
  }
  
  .changelog-entry__title {
    font-size: var(--font-size-3xl);
    font-weight: var(--font-weight-bold);
    color: var(--text-primary);
    line-height: 1.2;
    margin-bottom: var(--space-6);
  }
  
  .changelog-entry__info {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: var(--space-4);
  }
  
  .changelog-entry__details {
    display: flex;
    align-items: center;
    gap: var(--space-4);
    font-size: var(--font-size-sm);
    color: var(--text-secondary);
  }
  
  .changelog-entry__category {
    display: flex;
    align-items: center;
    gap: var(--space-1);
  }
  
  .changelog-entry__tags {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-2);
  }
  
  .changelog-entry__tag {
    font-size: var(--font-size-xs);
    color: var(--text-tertiary);
    background: var(--bg-secondary);
    padding: var(--space-1) var(--space-3);
    border-radius: var(--radius-full);
    border: 1px solid var(--border-light);
  }
  
  .changelog-entry__content {
    font-size: var(--font-size-lg);
    line-height: 1.7;
    color: var(--text-primary);
  }
  
  .changelog-entry__content :global(h2) {
    font-size: var(--font-size-xl);
    font-weight: var(--font-weight-semibold);
    margin: var(--space-8) 0 var(--space-4);
    color: var(--text-primary);
  }
  
  .changelog-entry__content :global(h3) {
    font-size: var(--font-size-lg);
    font-weight: var(--font-weight-semibold);
    margin: var(--space-6) 0 var(--space-3);
    color: var(--text-primary);
  }
  
  .changelog-entry__content :global(p) {
    margin-bottom: var(--space-4);
  }
  
  .changelog-entry__content :global(ul) {
    margin: var(--space-4) 0;
    padding-left: var(--space-6);
  }
  
  .changelog-entry__content :global(li) {
    margin-bottom: var(--space-2);
  }
  
  .changelog-entry__footer {
    margin-top: var(--space-8);
    padding-top: var(--space-6);
    border-top: 1px solid var(--border-light);
  }
  
  .share__title {
    font-size: var(--font-size-lg);
    font-weight: var(--font-weight-semibold);
    margin-bottom: var(--space-4);
  }
  
  .changelog-entry__sidebar {
    position: sticky;
    top: var(--space-8);
  }
  
  .sidebar-section {
    background: var(--bg-secondary);
    border-radius: var(--radius-lg);
    padding: var(--space-6);
    margin-bottom: var(--space-6);
  }
  
  .sidebar-section__title {
    font-size: var(--font-size-base);
    font-weight: var(--font-weight-semibold);
    margin-bottom: var(--space-4);
    color: var(--text-primary);
  }
  
  .sidebar-section__content {
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
  }
  
  .recent-entry {
    padding: var(--space-3);
    background: var(--bg-primary);
    border-radius: var(--radius-md);
    border: 1px solid var(--border-light);
    transition: var(--transition-all);
  }
  
  .recent-entry:hover {
    border-color: var(--border-medium);
  }
  
  .recent-entry__meta {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    margin-bottom: var(--space-2);
    font-size: var(--font-size-xs);
  }
  
  .recent-entry__type {
    color: var(--text-tertiary);
    font-weight: var(--font-weight-medium);
  }
  
  .recent-entry__version {
    background: var(--brand-primary-light);
    color: var(--brand-primary-dark);
    padding: var(--space-1) var(--space-2);
    border-radius: var(--radius-sm);
    font-weight: var(--font-weight-medium);
  }
  
  .recent-entry__title {
    display: block;
    font-size: var(--font-size-sm);
    font-weight: var(--font-weight-medium);
    color: var(--text-primary);
    text-decoration: none;
    line-height: 1.3;
    margin-bottom: var(--space-2);
    transition: var(--transition-colors);
  }
  
  .recent-entry__title:hover {
    color: var(--brand-primary);
  }
  
  .recent-entry__date {
    font-size: var(--font-size-xs);
    color: var(--text-tertiary);
  }
  
  .related-entries {
    background: var(--bg-secondary);
    padding: var(--space-12) 0;
    margin-top: var(--space-8);
  }
  
  @media (max-width: 1024px) {
    .changelog-entry__layout {
      grid-template-columns: 1fr;
      gap: var(--space-6);
    }
    
    .changelog-entry__sidebar {
      position: static;
      order: -1;
    }
  }
  
  @media (max-width: 768px) {
    .changelog-entry {
      padding: var(--space-4) 0;
    }
    
    .changelog-entry__title {
      font-size: var(--font-size-xl);
    }
    
    .changelog-entry__info {
      flex-direction: column;
      align-items: flex-start;
      gap: var(--space-3);
    }
    
    .changelog-entry__content {
      font-size: var(--font-size-base);
    }
    
    .sidebar-section {
      padding: var(--space-4);
    }
  }
</style>
